import {
  useMergeRefs,
  Flex,
  InputLeftElement,
  Box,
  Text,
  Stack,
  useBoolean,
  Popover,
  PopoverContent,
  PopoverTrigger,
  InputGroup,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModule } from '@starberry/components';
import Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import { countries } from 'countries-list';
import FocusLock from 'react-focus-lock';

import {
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumber,
  AsYouType,
} from 'libphonenumber-js';

function formatInput(target, country) {
  let val = target.value || '';

  if (val?.[0] !== '+') {
    val = '+' + getCountryCallingCode(country) + val;
  }

  if (val !== '') {
    try {
      const parsed = parsePhoneNumber(val, country);
      val = new AsYouType(country).input(
        '+' + getCountryCallingCode(country) + parsed.nationalNumber
      );
    } catch (err) {
      console.log(err);
    }
  }
  target.value = val;
}

export const PhoneInputField = React.forwardRef((props, outerRef) => {
  const { onChange, onBlur, inputProps, field = {}, defaultValue, defaultCountry = 'GB' } = props;
  const [country, setCountry] = useState(() => {
    let phone = null;
    try {
      phone = parsePhoneNumber(defaultValue);
    } catch (err) {}
    return phone?.country || defaultCountry;
  });
  const [isOpen, setCountriesList] = useBoolean(false);

  const Input = useModule('Input');

  const inputRef = useRef();
  const ref = useMergeRefs(inputRef, outerRef);

  useEffect(() => {
    if (inputRef.current) {
      formatInput(inputRef.current, country);
    }
  }, [inputRef, defaultValue, country]);

  const handleChange = useCallback(
    (e) => {
      onChange?.(e);
      formatInput(e.target, country);
    },
    [onChange, country]
  );

  const handleSelectCountry = useCallback(
    (e) => {
      setCountriesList.off();
      const newCountry = e.target.closest?.('[data-unicode]')?.getAttribute?.('data-unicode');
      if (!newCountry) {
        return;
      }
      setCountry(newCountry);
      if (ref.current) {
        formatInput(inputRef.current, newCountry);
        onChange?.({ target: ref });
      }
    },
    [onChange, setCountriesList, ref]
  );

  const CountryFlag = Flags[country];

  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'Enter':
          handleSelectCountry(e);
          return;
        case 'ArrowUp':
          e.stopPropagation();
          e.preventDefault();
          e.target?.previousSibling?.focus?.();
          return;
        case 'ArrowDown':
          e.stopPropagation();
          e.preventDefault();
          e.target?.nextSibling?.focus?.();
          return;
        default:
      }
    },
    [handleSelectCountry]
  );

  const handleFocusCountry = useCallback((e) => {
    const newCountry = e.target.closest?.('[data-unicode]')?.getAttribute?.('data-unicode');
    if (!newCountry) {
      return;
    }
    setCountry(newCountry);
  }, []);

  const countriesListElements = useMemo(() => {
    return Object.keys(countries)
      .filter((unicode) => hasFlag(unicode) && isSupportedCountry(unicode))
      .map((unicode, index) => {
        const CF = Flags[unicode];
        return (
          <Flex
            cursor="pointer"
            width="100%"
            p={2}
            onClick={handleSelectCountry}
            data-unicode={unicode}
            tabindex="0"
            _hover={{ bgColor: '#f0f0f0' }}
            _focus={{
              bgColor: '#f0f0f0',
            }}
            _focusVisible={{
              outline: 'none',
            }}
            onKeyDown={handleKeyDown}
            onFocus={handleFocusCountry}
          >
            <Flex alignItems="center" flexBasis="40px" flexShrink="0">
              <CF width={30} height={20} />
            </Flex>
            <Flex alignItems="center" flexShrink={1} flexGrow={1} minWidth={0}>
              <Text
                fontWeight="500"
                fontSize={16}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {countries[unicode].name}
              </Text>
            </Flex>
            <Flex
              alignItems="center"
              justifyContent="flex-end"
              flexBasis="45px"
              flexShrink="0"
              ml={2}
            >
              <Text fontSize={16} color="#a0a0a0" justifyContent="center">
                (+{getCountryCallingCode(unicode)})
              </Text>
            </Flex>
          </Flex>
        );
      });
  }, [handleSelectCountry, handleKeyDown, handleFocusCountry]);

  return (
    <Flex alignItems="center" justifyContent="center" position="relative">
      <InputGroup>
        <Popover placement="bottom-start" isOpen={isOpen} closeOnBlur={true} returnFocusOnClose>
          <InputLeftElement top="initial" left={2} zIndex={2}>
            <PopoverTrigger>
              <Box boxShadow="xs">
                <CountryFlag
                  width={30}
                  height={20}
                  cursor="pointer"
                  onClick={() => {
                    setCountriesList.toggle();
                  }}
                />
              </Box>
            </PopoverTrigger>
          </InputLeftElement>

          <PopoverContent zIndex={2000000} maxHeight="300px" overflowY="auto" borderRadius="sm">
            <Stack spacing={0} width="100%">
              <FocusLock autoFocus noFocusGuards persistentFocus returnFocus>
                {countriesListElements}
              </FocusLock>
            </Stack>
          </PopoverContent>
        </Popover>
        <Input
          {...field}
          {...inputProps}
          onChange={() => handleChange}
          defaultValue={defaultValue}
          onBlur={() => onBlur}
          ref={ref}
          pl={'45px'}
        />
      </InputGroup>
    </Flex>
  );
});

export default React.memo(PhoneInputField);
